import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Home, CalendarDays, Users, Newspaper, PartyPopper, Plane, Handshake, FileText, Heart, ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, inputCls } from "@/components/kit";
import { api, errMsg, fmtDate, initials, onColor } from "@/lib/api";

export function hubPages(data) {
  return [
    { key: "", label: "Home", icon: Home },
    { key: "fixtures", label: "Fixtures", icon: CalendarDays },
    { key: "squad", label: "Squad", icon: Users },
    { key: "news", label: "News", icon: Newspaper },
    { key: "events", label: "Events", icon: PartyPopper },
    ...(data.shop ? [{ key: "shop", label: "Shop", icon: ShoppingBag }] : []),
    ...(data.travel ? [{ key: "travel", label: "Travel", icon: Plane }] : []),
    ...(data.features?.sponsors ? [{ key: "sponsors", label: "Sponsors", icon: Handshake }] : []),
    ...(data.embeds.length ? [{ key: "forms", label: "Forms", icon: FileText }] : []),
    { key: "connect", label: "Follow", icon: Heart },
  ];
}

export const BottomNav = ({ slug, pages, color }) => {
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => {
    ref.current?.querySelector("a.active")?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [pathname]);
  return (
  <nav className="fixed inset-x-0 bottom-3 z-50 flex justify-center px-3" data-testid="hub-bottom-nav">
    <div ref={ref} className="no-scrollbar flex max-w-full gap-1 overflow-x-auto rounded-full border border-white/10 p-1.5 shadow-2xl backdrop-blur-xl" style={{ background: "color-mix(in srgb, var(--hub-card) 88%, transparent)" }}>
      {pages.map((p) => (
        <NavLink key={p.key} end to={`/t/${slug}${p.key ? `/${p.key}` : ""}`} data-testid={`hub-nav-${p.key || "home"}`}
          className="flex shrink-0 flex-col items-center gap-0.5 rounded-full px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors"
          style={({ isActive }) => (isActive ? { background: color, color: onColor(color) } : { color: "var(--hub-muted)" })}>
          <p.icon className="h-4 w-4" /> {p.label}
        </NavLink>
      ))}
    </div>
  </nav>
  );
};

export const PageTitle = ({ children, sub }) => (
  <div className="mb-8">
    <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">{children}</h1>
    {sub && <p className="mt-2 text-slate-400">{sub}</p>}
  </div>
);

export const VsCard = ({ team, e, color, slug }) => (
  <div className="rounded-lg border border-white/[0.08] bg-[#111827] p-6" data-testid="hub-vs-card">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--team-ink)" }}>Next fixture{e.competition ? ` · ${e.competition}` : ""}</p>
    <div className="mt-5 flex items-center justify-between gap-3">
      {[team.name, e.opponent || e.title].map((n, i) => (
        <div key={i} className={`flex flex-1 flex-col items-center gap-2 text-center ${i === 1 ? "order-3" : ""}`}>
          <span className="flex h-14 w-14 items-center justify-center rounded-full font-display text-lg font-black" style={i === 0 ? { background: color, color: onColor(color) } : { background: "var(--hub-soft)", color: "var(--hub-text)" }}>{initials(n)}</span>
          <span className="text-sm font-semibold text-white">{n}</span>
        </div>
      ))}
      <span className="order-2 font-display text-2xl font-black text-slate-500">VS</span>
    </div>
    <p className="mt-5 text-center text-sm text-slate-400">{fmtDate(e.starts_at)} · {e.home_away === "home" ? "Home" : e.home_away === "away" ? "Away" : e.venue}</p>
    <Link to={`/t/${slug}/fixtures`} className="mt-4 block text-center text-sm font-bold uppercase tracking-wider" style={{ color: "var(--team-ink)" }} data-testid="hub-match-centre-link">View fixtures →</Link>
  </div>
);

export const LinkCard = ({ over, title, text, to, cta, testid }) => (
  <Link to={to} className="group block rounded-lg border border-white/[0.08] bg-[#111827] p-6 transition-transform hover:-translate-y-0.5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--team-ink)" }}>{over}</p>
    <p className="mt-2 font-display text-2xl font-bold uppercase text-white">{title}</p>
    <p className="mt-1 text-sm text-slate-400">{text}</p>
    <p className="mt-4 flex items-center gap-1 text-sm font-bold text-white">{cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></p>
  </Link>
);

export const TravelCard = ({ travel, slug, color }) => (
  <div className="relative overflow-hidden rounded-lg border border-white/[0.08] p-6 sm:p-8" style={{ background: `linear-gradient(135deg, ${color}26, var(--hub-card) 60%)` }} data-testid="hub-travel-card">
    <p className="font-display text-3xl font-black uppercase text-white">{travel.title}</p>
    <p className="mt-2 max-w-md text-slate-300">{travel.text}</p>
    <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
      {travel.cta && <Button asChild className="rounded-full font-bold uppercase" style={{ background: color, color: onColor(color) }} data-testid="hub-travel-cta"><Link to={`/t/${slug}/travel`}>{travel.cta} →</Link></Button>}
      {travel.partner && <span className="font-display text-lg font-black uppercase tracking-widest text-slate-400">{travel.partner}</span>}
    </div>
  </div>
);

export const TravelPage = ({ travel, slug, color }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", destination: "", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/public/teams/${slug}/travel-enquiry`, f);
      setSent(true);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-8">
      <TravelCard travel={{ ...travel, cta: "" }} slug={slug} color={color} />
      {travel.link && <a href={travel.link} target="_blank" rel="noreferrer" className="block text-sm font-bold" style={{ color: "var(--team-ink)" }} data-testid="travel-external-link">Visit {travel.partner || "our travel partner"} →</a>}
      <div className="rounded-lg border border-white/[0.08] bg-[#111827] p-6">
        <p className="font-display text-2xl font-bold uppercase text-white">Holiday enquiry</p>
        {sent ? <p className="mt-3 text-slate-300" data-testid="travel-sent-msg">Thanks! A travel expert will be in touch soon.</p> : (
          <form onSubmit={submit} className="mt-4 space-y-4" data-testid="travel-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name"><Input required value={f.name} onChange={set("name")} data-testid="travel-name-input" /></Field>
              <Field label="Email"><Input type="email" required value={f.email} onChange={set("email")} data-testid="travel-email-input" /></Field>
              <Field label="Phone"><Input value={f.phone} onChange={set("phone")} data-testid="travel-phone-input" /></Field>
              <Field label="Where & when?"><Input value={f.destination} onChange={set("destination")} placeholder="e.g. Spain, July, 2 adults 2 kids" data-testid="travel-destination-input" /></Field>
            </div>
            <Field label="Anything else"><textarea className={`${inputCls} h-24`} value={f.message} onChange={set("message")} data-testid="travel-message-input" /></Field>
            <Button type="submit" className="rounded-full px-8 font-bold uppercase" style={{ background: color, color: onColor(color) }} data-testid="travel-submit-btn">Send enquiry</Button>
          </form>
        )}
      </div>
    </div>
  );
};
